import contentCache from './ContentCache';
import { extractLoopMediaAssets } from './LoopMediaPrefetch';
import { putNativeLoopManifestSnapshot } from './NativeLoopOfflineBridge';

export const LOOP_MANIFEST_CACHE_MAX_AGE_MS = 72 * 60 * 60 * 1000;

const CACHE_PREFIX = 'trillboards:loop-manifest-lkg:v1';

export const buildLoopManifestCacheKey = (screenId) =>
  `${CACHE_PREFIX}:${String(screenId || 'unknown')}`;

const getStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
  } catch (_) {}
  return null;
};

export const hasLoopComposerManifest = (content) => {
  const policy = content?.playback_policy;
  const loopPlan = policy?.schedule_policy?.source === 'loop_plan'
    ? policy?.loop_plan
    : null;

  return Boolean(
    policy?.delivery_mode === 'loop_composer'
    && Array.isArray(loopPlan?.segments)
    && loopPlan.segments.length > 0
  );
};

export const countLoopManifestAssets = (content) => {
  if (!content || typeof content !== 'object') {
    return 0;
  }

  return extractLoopMediaAssets(content).length;
};

export const buildLoopManifestCacheRecord = (screenId, envelope, metadata = {}) => {
  const content = envelope?.data;
  if (!screenId || !envelope?.status || !hasLoopComposerManifest(content)) {
    return null;
  }

  const savedAtMs = Number(metadata.nowMs) || Date.now();
  return {
    version: 1,
    screen_id: String(screenId),
    endpoint: metadata.endpoint || null,
    etag: metadata.etag || null,
    saved_at_ms: savedAtMs,
    saved_at: new Date(savedAtMs).toISOString(),
    cached_asset_count: countLoopManifestAssets(content),
    envelope,
  };
};

const readLocalStorageRecord = (key) => {
  try {
    const storage = getStorage();
    const raw = storage?.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const writeLocalStorageRecord = (key, record) => {
  try {
    const storage = getStorage();
    if (!storage) {
      return false;
    }
    storage.setItem(key, JSON.stringify(record));
    return true;
  } catch (_) {
    return false;
  }
};

export const normalizeCachedLoopManifest = (record, options = {}) => {
  if (!record || typeof record !== 'object') {
    return { hit: false, reason: 'miss' };
  }

  const nowMs = Number(options.nowMs) || Date.now();
  const maxAgeMs = Number(options.maxAgeMs) || LOOP_MANIFEST_CACHE_MAX_AGE_MS;
  const savedAtMs = Number(record.saved_at_ms);
  const ageMs = Number.isFinite(savedAtMs) ? Math.max(0, nowMs - savedAtMs) : null;

  if (!Number.isFinite(savedAtMs)) {
    return { hit: false, reason: 'invalid_timestamp' };
  }

  if (Number.isFinite(maxAgeMs) && ageMs > maxAgeMs) {
    return { hit: false, reason: 'stale', ageMs };
  }

  const envelope = record.envelope;
  const content = envelope?.data;
  if (!envelope?.status || !hasLoopComposerManifest(content)) {
    return { hit: false, reason: 'invalid_manifest', ageMs };
  }

  return {
    hit: true,
    source: options.source || record.source || 'unknown',
    record,
    envelope,
    content,
    etag: record.etag || null,
    ageMs,
    cachedAssetCount: Number(record.cached_asset_count) || countLoopManifestAssets(content),
  };
};

export const saveLoopManifestCache = async (screenId, envelope, metadata = {}) => {
  const key = buildLoopManifestCacheKey(screenId);
  const record = buildLoopManifestCacheRecord(screenId, envelope, metadata);
  if (!record) {
    return { saved: false, reason: 'not_loop_manifest', key };
  }

  let indexedDbSaved = false;
  try {
    if (contentCache && typeof contentCache.cacheAPIResponse === 'function') {
      indexedDbSaved = await contentCache.cacheAPIResponse(key, record, screenId);
    }
  } catch (_) {
    indexedDbSaved = false;
  }

  const localStorageSaved = writeLocalStorageRecord(key, record);
  const nativeSaved = putNativeLoopManifestSnapshot(screenId, metadata.fingerprint, record);

  return {
    saved: Boolean(indexedDbSaved || localStorageSaved),
    key,
    indexedDbSaved: Boolean(indexedDbSaved),
    localStorageSaved,
    nativeSaved,
    record,
  };
};

export const loadLoopManifestCache = async (screenId, options = {}) => {
  const key = buildLoopManifestCacheKey(screenId);
  const maxAgeMs = Number(options.maxAgeMs) || LOOP_MANIFEST_CACHE_MAX_AGE_MS;
  const nowMs = Number(options.nowMs) || Date.now();

  let record = null;
  try {
    if (contentCache && typeof contentCache.getCachedResponse === 'function') {
      record = await contentCache.getCachedResponse(key, maxAgeMs);
      const normalized = normalizeCachedLoopManifest(record, {
        source: 'indexeddb',
        maxAgeMs,
        nowMs,
      });
      if (normalized.hit) {
        return { ...normalized, key };
      }
    }
  } catch (_) {}

  record = readLocalStorageRecord(key);
  const normalized = normalizeCachedLoopManifest(record, {
    source: 'localStorage',
    maxAgeMs,
    nowMs,
  });

  return { ...normalized, key };
};
